import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateRoleInput,
  PaginatedResult,
  Role,
  SortDirection,
  UpdateRoleInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface RoleListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: SortDirection;
}

const roleKeys = {
  all: ['roles'] as const,
  list: (params: RoleListParams) => ['roles', 'list', params] as const,
  detail: (id: string) => ['roles', 'detail', id] as const,
};

export function useRoles(params: RoleListParams): UseQueryResult<PaginatedResult<Role>> {
  return useQuery({
    queryKey: roleKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Role>>(
        `/roles${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateRole(): UseMutationResult<Role, unknown, CreateRoleInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => api.post<Role>('/roles', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

export interface UpdateRoleArgs {
  id: string;
  input: UpdateRoleInput;
}

export function useUpdateRole(): UseMutationResult<Role, unknown, UpdateRoleArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateRoleArgs) => api.patch<Role>(`/roles/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}

export function useDeleteRole(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/roles/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.all });
    },
  });
}
